import React from 'react';
import { Hourglass, Check } from 'lucide-react';
import { Transition } from '@headlessui/react';
import TextBlock from '@/components/(features)/surveyWizard/layout/TextBlock';
import MediaBlock from '@/components/(features)/surveyWizard/layout/MediaBlock';
import ButtonBlock from '@/components/(features)/surveyWizard/layout/ButtonBlock';
import StepButtonBlock from '@/components/(features)/surveyWizard/layout/StepButtonBlock';
import CheckboxBlock from '@/components/(features)/surveyWizard/layout/CheckboxBlock';
import LoginFormComponent from '@/components/(dashboard)/auth/LoginFormComponent';
import { Input } from '@/components/ui/input';
import { BiSolidEditAlt } from 'react-icons/bi';
import Image from 'next/image';
import PopupVideo from '@/components/(features)/surveyWizard/layout/PopupVideo';
import { safeHtmlLinks } from '@/utils/links/safeHref';

const PopupCountdown: React.FC<{ timer: string }> = ({ timer }) => {
    const [hours, setHours] = React.useState('00');
    const [minutes, setMinutes] = React.useState('00');
    const [seconds, setSeconds] = React.useState('00');

    React.useEffect(() => {
        const parts = timer.split(':').map(Number);
        const h = parts[0] || 0;
        const m = parts[1] || 0;
        const s = parts[2] || 0;
        const targetSeconds = h * 3600 + m * 60 + s;
        const startTime = Date.now();

        const interval = setInterval(() => {
            const now = Date.now();
            const endTime = startTime + targetSeconds * 1000;
            const distance = endTime - now;

            if (distance <= 0) {
                setHours('00');
                setMinutes('00');
                setSeconds('00');
                clearInterval(interval);
            } else {
                const hrs = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const mins = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
                const secs = Math.floor((distance % (1000 * 60)) / 1000);

                setHours(String(hrs).padStart(2, '0'));
                setMinutes(String(mins).padStart(2, '0'));
                setSeconds(String(secs).padStart(2, '0'));
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [timer]);

    return (
        <div className='flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 font-medium'>
            <Hourglass className='size-4 shrink-0 text-[#EEAC43]' />
            <span>زمان باقی مانده</span>
            <span className='font-bold text-gray-700 dark:text-white'>{hours}</span>
            <span>ساعت</span>
            <span className='font-bold text-gray-700 dark:text-white'>{minutes}</span>
            <span>دقیقه</span>
            <span className='font-bold text-gray-700 dark:text-white'>{seconds}</span>
            <span>ثانیه</span>
        </div>
    );
};

interface SurveyStepRendererProps {
    showGroupFieldsBeforeMobile?: any;
    landingSlug?: string;
    survey: Array<{ block: any[] }>;
    currentStep: number;
    totalSteps: number;
    hasExtraStep: boolean;
    phone: string;
    isLoggedInGlobal: boolean;
    isEditing: boolean;
    submitting: boolean;
    setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
    setPhone: React.Dispatch<React.SetStateAction<string>>;
    setHasRegistered: React.Dispatch<React.SetStateAction<boolean>>;
    handleStepButtonClick: (stepId: string, answer: string, isFinish?: boolean) => void;
    handleConfirmRegistration: () => void;
    urlWithoutProtocol: string;
    selectedWizard?: string | undefined;
    user?: string;
    setFullName: React.Dispatch<React.SetStateAction<string>>;
    fullName?: string | undefined;
    email?: string;
    setEmail: React.Dispatch<React.SetStateAction<string>>;
    onPrevious: () => void;
    percent?: number;
    timer?: string;
    handleConfirmClosePopup?: (() => void) | undefined;
}

const SurveyStepRenderer: React.FC<SurveyStepRendererProps> = ({
    showGroupFieldsBeforeMobile,
    landingSlug,
    survey,
    currentStep,
    totalSteps,
    hasExtraStep,
    phone,
    isLoggedInGlobal,
    isEditing,
    submitting,
    setIsEditing,
    setPhone,
    setHasRegistered,
    handleStepButtonClick,
    handleConfirmRegistration,
    urlWithoutProtocol,
    selectedWizard,
    setFullName,
    fullName,
    email,
    setEmail,
    onPrevious,
    percent,
    timer,
    handleConfirmClosePopup,
}) => {
    const isStepIndicatorVisible = currentStep > (hasExtraStep ? 1 : 0);

    return (
        <>
            {/* Registration Step */}
            <Transition
                show={currentStep === (hasExtraStep ? 1 : 0)}
                enter='transition-all transform duration-500 linear'
                enterFrom='translate-y-7 opacity-0'
                enterTo='translate-y-0 opacity-100'
                leave='transition-all transform duration-100 linear'
                leaveFrom='translate-y-0 opacity-100'
                leaveTo='translate-y-7 opacity-0'
                as='div'
                className={`w-full px-4 md:px-6 pb-6 flex flex-col items-center justify-between ${
                    isStepIndicatorVisible ? 'pt-2' : 'pt-10'
                } ${
                    currentStep === (hasExtraStep ? 1 : 0)
                        ? 'relative'
                        : 'absolute inset-x-0 top-0 pointer-events-none'
                }`}
            >
                <div className='flex flex-col w-full space-y-3 md:space-y-4'>
                    {showGroupFieldsBeforeMobile && (
                        <div className='flex flex-col items-center space-y-3 text-center w-full'>
                            <div className='flex flex-col items-center space-y-2'>
                                <h2
                                    className={`text-2xl px-2 max-w-[90%] lg:px-6 md:text-3xl font-black ${selectedWizard === 'wizard2' ? 'text-white' : 'text-gray-900 dark:text-white'} leading-snug`}
                                >
                                    {showGroupFieldsBeforeMobile.title}
                                </h2>
                                <div
                                    className={`leading-relaxed max-w-[90%] max-lg:text-sm [&>span]:text-[10px] ${selectedWizard === 'wizard2' ? 'text-[#EEAC43] [&>p]:text-white/70' : 'text-[#EEAC43] [&>p]:text-black/70 dark:[&>p]:text-white/70'}`}
                                    dangerouslySetInnerHTML={{
                                        __html: safeHtmlLinks(showGroupFieldsBeforeMobile.text),
                                    }}
                                />
                            </div>

                            {/* Hide video/image and subtitle when name/email form is visible to prevent overflow */}
                            {!(isLoggedInGlobal && !isEditing) &&
                                showGroupFieldsBeforeMobile?.bannerType === 'video' && (
                                    <div className='w-full aspect-video rounded-xl overflow-hidden shadow-lg border border-gray-100 dark:border-white/10'>
                                        <PopupVideo
                                            video={showGroupFieldsBeforeMobile.video}
                                            embedClassName='w-full h-full'
                                        />
                                    </div>
                                )}
                            {!(isLoggedInGlobal && !isEditing) &&
                                showGroupFieldsBeforeMobile?.bannerType === 'image' && (
                                    <div className='w-full relative h-auto rounded-2xl overflow-hidden !mt-0'>
                                        <Image
                                            className='w-full h-full object-cover'
                                            width={1800}
                                            height={1012}
                                            src={
                                                showGroupFieldsBeforeMobile?.image?.sourceUrl || ''
                                            }
                                            alt='wizard banner'
                                        />
                                    </div>
                                )}
                            {!(isLoggedInGlobal && !isEditing) &&
                                showGroupFieldsBeforeMobile?.subtitle && (
                                    <div
                                        className='text-xs text-gray-500 dark:text-gray-400 font-medium !mt-0 text-center leading-relaxed [&_del]:line-through [&_p]:mb-1 [&_br]:block'
                                        dangerouslySetInnerHTML={{
                                            __html: safeHtmlLinks(
                                                showGroupFieldsBeforeMobile.subtitle,
                                            ),
                                        }}
                                    />
                                )}
                        </div>
                    )}
                </div>

                <div className='w-full flex flex-col items-center mt-4'>
                    {/* Capacity Indicator */}
                    {percent !== undefined && (
                        <div className='flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-4 font-bold select-none justify-center'>
                            <span className='size-2 rounded-full bg-[#FE4657] animate-pulse' />
                            <span>فقط {percent} نفر از ۲۵ ظرفیت باقیست</span>
                        </div>
                    )}

                    {isLoggedInGlobal && !isEditing ? (
                        <div className='w-full flex flex-col gap-2.5 mt-2'>
                            {/* Compact phone confirmation row */}
                            <div
                                className={` hidden items-center justify-between gap-2 w-full px-3 py-2 rounded-xl border ${selectedWizard === 'wizard2' ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 dark:bg-white/5 border-gray-200 dark:border-white/10 text-gray-700 dark:text-white'}`}
                            >
                                <span className='size-5 rounded-full bg-green-500 flex items-center justify-center shrink-0'>
                                    <Check size={11} className='text-white stroke-[3]' />
                                </span>
                                <p className='text-xs opacity-80 flex-1 text-right'>
                                    کد تأیید به <span className='font-bold font-mono'>{phone}</span>{' '}
                                    ارسال شد
                                </p>
                                <button
                                    type='button'
                                    className='text-[#EEAC43] hover:brightness-110 transition shrink-0'
                                    onClick={() => setIsEditing(true)}
                                >
                                    <BiSolidEditAlt size={16} />
                                </button>
                            </div>

                            {/* Name + Email inputs */}
                            <Input
                                className='border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 py-3 px-4 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/30 focus-visible:outline-0 focus-visible:ring-1 focus-visible:ring-[#EEAC43] rounded-xl shadow-none w-full'
                                type='text'
                                required
                                autoComplete='off'
                                value={fullName}
                                placeholder='نام و نام خانوادگی'
                                onChange={(e) => setFullName(e.target.value)}
                                id='fullname'
                            />
                            <Input
                                className='border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 py-3 px-4 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/30 focus-visible:outline-0 focus-visible:ring-1 focus-visible:ring-[#EEAC43] rounded-xl shadow-none w-full'
                                type='email'
                                dir='ltr'
                                required
                                autoComplete='off'
                                value={email}
                                placeholder='email@example.com'
                                onChange={(e) => setEmail(e.target.value)}
                                id='userEmail'
                            />

                            <button
                                type='button'
                                className='w-full py-3.5 px-6 bg-gradient-to-r from-[#e7c282] to-[#b48336] hover:brightness-105 text-black font-bold rounded-xl transition-all disabled:opacity-50'
                                onClick={handleConfirmRegistration}
                                disabled={!fullName || !email || submitting}
                            >
                                {submitting ? 'در حال ارسال...' : 'تأیید و ادامه'}
                            </button>
                        </div>
                    ) : (
                        <div className='w-full flex flex-col'>
                            <LoginFormComponent
                                title={'برای رزرو هدیه رایگان، شماره موبایل خود را وارد کنید'}
                                selectedWizard={selectedWizard}
                                httpReferrer={urlWithoutProtocol}
                                textBtn={'ثبت نهایی'}
                                otpBtnText={'تأیید'}
                                landingSlug={
                                    landingSlug ||
                                    (selectedWizard === 'wizard2' ? 'nokhbegan-popup' : 'wizard')
                                }
                                customHeight={'!h-auto'}
                                handleLoginSuccess={() => setHasRegistered(true)}
                                popupWizardStyle={true}
                            />
                            {isEditing && (
                                <button
                                    type='button'
                                    className=' max-lg:text-xs text-primary dark:text-white mt-5'
                                    onClick={() => {
                                        setPhone(phone);
                                        setIsEditing(false);
                                    }}
                                >
                                    انصراف و بازگشت
                                </button>
                            )}
                        </div>
                    )}

                    {/* Countdown Timer Footer */}
                    {timer && !(isLoggedInGlobal && !isEditing) && (
                        <div className='w-full flex justify-center items-center pt-4 border-t border-gray-100 dark:border-white/10 mt-3 select-none'>
                            <PopupCountdown timer={timer} />
                        </div>
                    )}
                </div>
            </Transition>

            {/* Survey Steps */}
            {survey.map((surveyItem, idx) => {
                const stepIndex = idx + (hasExtraStep ? 2 : 1);
                const isLastStep = stepIndex === totalSteps - 1;
                const hasButtonInCurrentStep = surveyItem.block?.some(
                    (b) =>
                        b.fieldGroupName ===
                            'SitesContentSettings_Generaloptionssetting_wizardComponent_survey_Block_Button' &&
                        b.button?.url,
                );

                return (
                    <Transition
                        key={`survey-item-${idx}`}
                        show={stepIndex === currentStep}
                        enter='transition-all transform duration-700 ease-out'
                        enterFrom='translate-y-5 opacity-0'
                        enterTo='translate-y-0 opacity-100'
                        leave='transform transiton-all absolute duration-50 ease-in'
                        leaveFrom='translate-y-0 opacity-100'
                        leaveTo='translate-y-5 opacity-0'
                        as='div'
                        className={`w-full px-4 overflow-hidden md:px-6 pb-4 md:pb-6 flex flex-col items-center justify-between ${
                            isStepIndicatorVisible ? 'pt-3 md:pt-5' : 'pt-6 md:pt-10'
                        } ${currentStep === totalSteps - 1 ? 'pb-4 md:pb-6' : ''} ${
                            stepIndex === currentStep
                                ? 'relative'
                                : 'absolute inset-x-0 top-0 pointer-events-none'
                        }`}
                    >
                        <div
                            className={`flex flex-col w-full space-y-2 md:space-y-3.5 ${
                                stepIndex === totalSteps - 1
                                    ? 'items-center text-center [&_*]:text-center [&_*]:mx-auto [&_img]:max-h-[140px] [&_img]:sm:max-h-[180px] [&_img]:w-auto [&_img]:object-contain'
                                    : ''
                            }`}
                        >
                            {surveyItem.block.map((block, blockIdx) => {
                                const uniqueKey = `block-${idx}-${blockIdx}`;

                                switch (block.fieldGroupName) {
                                    case 'SitesContentSettings_Generaloptionssetting_wizardComponent_survey_Block_Text':
                                        return (
                                            <TextBlock
                                                className={
                                                    selectedWizard === 'wizard2'
                                                        ? '[&>*]:!text-white prose-strong:!text-white'
                                                        : ''
                                                }
                                                key={uniqueKey}
                                                text={block.text || ''}
                                            />
                                        );
                                    case 'SitesContentSettings_Generaloptionssetting_wizardComponent_survey_Block_Media':
                                        return (
                                            <MediaBlock
                                                key={uniqueKey}
                                                imageVideoRadioButton={
                                                    block.imageVideoRadioButton || ''
                                                }
                                                video={block.video}
                                                image={block.image}
                                            />
                                        );
                                    case 'SitesContentSettings_Generaloptionssetting_wizardComponent_survey_Block_Checkbox':
                                        return (
                                            <CheckboxBlock
                                                key={uniqueKey}
                                                text={block.text || ''}
                                            />
                                        );
                                    case 'SitesContentSettings_Generaloptionssetting_wizardComponent_survey_Block_Button':
                                        return (
                                            <ButtonBlock
                                                key={uniqueKey}
                                                button={block.button}
                                                isLastStep={isLastStep}
                                            />
                                        );
                                    case 'SitesContentSettings_Generaloptionssetting_wizardComponent_survey_Block_StepButton':
                                        return (
                                            <StepButtonBlock
                                                key={uniqueKey}
                                                singleMultiRadioButton={
                                                    block.singleMultiRadioButton
                                                }
                                                singleText={block.singleText}
                                                multiText={block.multiText}
                                                finishButton={block.finishButton}
                                                onStepButtonClick={(answer) =>
                                                    handleStepButtonClick(
                                                        `step-${stepIndex}`,
                                                        answer,
                                                        !!block.finishButton,
                                                    )
                                                }
                                            />
                                        );
                                    default:
                                        return null;
                                }
                            })}
                        </div>

                        {onPrevious && (
                            <button
                                type='button'
                                onClick={onPrevious}
                                className={
                                    stepIndex === totalSteps - 1
                                        ? hasButtonInCurrentStep
                                            ? 'hidden'
                                            : 'w-full py-3 px-6 bg-gradient-to-r from-[#e7c282] to-[#b48336] hover:brightness-105 text-black font-bold rounded-xl transition-all text-center text-base mt-8'
                                        : 'hidden'
                                }
                            >
                                <span>بازگشت به مرحله قبل</span>
                            </button>
                        )}
                        {stepIndex === totalSteps - 1 && (
                            <button
                                type='button'
                                onClick={handleConfirmClosePopup}
                                className={
                                    hasButtonInCurrentStep
                                        ? 'hidden'
                                        : 'w-full py-3 px-6 bg-gray-100 text-black dark:bg-white/10 dark:text-white font-bold rounded-xl transition-all text-center text-base mt-2'
                                }
                            >
                                <span>پایان و بستن</span>
                            </button>
                        )}
                    </Transition>
                );
            })}
        </>
    );
};

export default SurveyStepRenderer;
